import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ExpandableSection,
  Link,
  Modal,
  SpaceBetween,
  Tiles,
} from "@amzn/awsui-components-react";

/** The redrives this case can launch itself. */
export type RedriveOptionId = "dlq" | "document" | "delete";

interface InCaseOption {
  id: RedriveOptionId;
  title: string;
  tagline: string;
  /** Three short bullets, shown only for the selected option. */
  effects: string[];
}

export const REDRIVE_OPTIONS: InCaseOption[] = [
  {
    id: "dlq",
    title: "DLQ redrive",
    tagline: "Replay the failed DLQ messages",
    effects: [
      "DLQ: messages are requeued",
      "CBEs: published if the event now succeeds",
      "Status: Executing → Monitoring (returns reopen the case)",
    ],
  },
  {
    id: "document",
    title: "Order redrive",
    tagline: "Rebuild the CBEs, then clear the DLQ",
    effects: [
      "CBEs: Lusca redrives each transaction's accounting group and document, rebuilding the CBEs",
      "DLQ: untouched until you check the CBEs and run the DLQ redrive",
      "Status: Executing → Ready to redrive (DLQ)",
    ],
  },
  {
    id: "delete",
    title: "Delete without redrive",
    tagline: "Remove the items (needs an approved MCM)",
    effects: [
      "DLQ: items are deleted, verified in the next snapshot",
      "CBEs: none",
      "Status: Draft MCM → Executing → Resolved once verified",
    ],
  },
];

const INGESTION_DOC =
  "https://code.amazon.com/packages/PVAccountingDevDocs/blobs/mainline/--/docs/system-specs/modules/Ingestion_system-overview.md";

/** Fixes run outside Lusca; each still needs a DLQ redrive afterwards to clear the items. */
const UPSTREAM_OPTIONS = [
  {
    title: "FOIL transaction redispatch",
    when: "Lusca didn't ingest the FOIL event correctly",
    href: "https://code.amazon.com/packages/CrocDoc/blobs/mainline/--/oncall/operation_tool/chunkstore_redispatch.md",
    label: "SOP",
  },
  {
    title: "FOIL redrive",
    when: "FOIL didn't ingest DOWEN correctly (under 2 years old)",
    href: `${INGESTION_DOC}#redrive-types`,
    label: "Docs",
  },
  {
    title: "Royalty payment redrive",
    when: "Payment Tracker data for a cost document was corrected",
    href: "/payment-redrive",
    label: "Payment Redrive",
  },
];

interface RedriveOptionsModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Continue with the chosen option (opens its confirm dialog). */
  onChoose: (id: RedriveOptionId) => void;
  /** Why an option cannot be launched now (undefined = available). */
  disabledReason: (id: RedriveOptionId) => string | undefined;
  /** Preselected and labelled "Next step" (e.g. the DLQ redrive after a order redrive). */
  recommended?: RedriveOptionId;
  itemCount: number;
  /** The stage is redriven in Lusca Search Prod and linked back (temporary). */
  external?: boolean;
}

const firstAvailable = (
  disabledReason: (id: RedriveOptionId) => string | undefined,
  recommended?: RedriveOptionId
): RedriveOptionId =>
  recommended ??
  REDRIVE_OPTIONS.find((o) => !disabledReason(o.id))?.id ??
  "dlq";

export const RedriveOptionsModal: React.FC<RedriveOptionsModalProps> = ({
  visible,
  onDismiss,
  onChoose,
  disabledReason,
  recommended,
  itemCount,
  external = false,
}) => {
  const [selected, setSelected] = useState<RedriveOptionId>(
    firstAvailable(disabledReason, recommended)
  );
  // Re-pick the default each time the dialog opens (the case may have moved on).
  useEffect(() => {
    if (visible) setSelected(firstAvailable(disabledReason, recommended));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on open
  }, [visible]);

  const option = REDRIVE_OPTIONS.find((o) => o.id === selected)!;
  const blocked = disabledReason(selected);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`How should these ${itemCount.toLocaleString()} items be handled?`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!!blocked}
              onClick={() => {
                onDismiss();
                onChoose(selected);
              }}
            >
              Continue
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {external && (
          <Box color="text-body-secondary" data-testid="external-redrive-note">
            Prod redrives run in Lusca Search Prod for now; Continue shows you
            how, and you link the run back here.
          </Box>
        )}
        <Tiles
          value={selected}
          onChange={({ detail }) =>
            setSelected(detail.value as RedriveOptionId)
          }
          columns={1}
          items={REDRIVE_OPTIONS.map((o) => {
            const reason = disabledReason(o.id);
            return {
              value: o.id,
              label: (
                <SpaceBetween
                  direction="horizontal"
                  size="xs"
                  alignItems="center"
                >
                  <span data-testid={`redrive-option-${o.id}`}>{o.title}</span>
                  {recommended === o.id && (
                    <Badge color="green">Next step</Badge>
                  )}
                </SpaceBetween>
              ),
              description: reason ? `${o.tagline} · ${reason}` : o.tagline,
              disabled: !!reason,
            };
          })}
        />
        <Box data-testid="redrive-option-effects">
          <Box variant="awsui-key-label">What happens</Box>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {option.effects.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Box>
        <ExpandableSection
          variant="footer"
          headerText="Fix upstream instead?"
          headerDescription="FOIL redispatch, FOIL redrive, royalty payment redrive — run outside Lusca, then a DLQ redrive here"
        >
          <SpaceBetween size="xxs">
            {UPSTREAM_OPTIONS.map((u) => (
              <div key={u.title}>
                <strong>{u.title}</strong> — {u.when} ·{" "}
                <Link
                  href={u.href}
                  external
                  externalIconAriaLabel="Opens in a new tab"
                >
                  {u.label}
                </Link>
              </div>
            ))}
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Modal>
  );
};

export default RedriveOptionsModal;
